/**
 * Chart data → CSV, entirely client-side (US 2488, SPEC/2488).
 *
 * `toCsv` is generic JSON-rows → CSV and knows nothing about charts;
 * `chartDataToCsv` feeds it exactly the rows a chart draws; `downloadCsv` hands
 * the text to the browser as a file. The modebar's "Download data" button is the
 * three chained, so every chart type exports through the same code.
 */

import type { ChartConfig } from '../model';
import { normalizeConfig, type DatasetResolver } from '../normalize';

/**
 * The field separator — the one place to change it. `;` because Excel in a
 * decimal-comma locale (es-CL) splits a double-clicked CSV on its list separator,
 * which is `;` there; a `,` file would open as a single column.
 */
export const SEPARATOR: string = ';';
const EOL = '\r\n';
/** Needs quoting under RFC 4180: the separator, a quote, or a line break. */
const NEEDS_QUOTES = new RegExp(`["\r\n${SEPARATOR.replace(/[\\^\]-]/g, '\\$&')}]`);
/**
 * A spreadsheet runs a cell starting with one of these as a formula. Category
 * labels come from files users upload, so a label like `=HYPERLINK(…)` would
 * otherwise execute on open. Strings only: a negative number stays `-3`.
 */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

function cell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  let text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  if (typeof value === 'string' && FORMULA_LEAD.test(text)) text = `'${text}`;
  return NEEDS_QUOTES.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * Rows of JSON objects → CSV text (RFC 4180, CRLF-terminated lines).
 *
 * Columns are the union of every row's keys in first-appearance order: rows are
 * not guaranteed uniform, and reading the header off the first row would silently
 * drop a column. No rows, or rows with no keys, give `''`.
 */
export function toCsv(rows: Record<string, unknown>[]): string {
  const columns: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        columns.push(key);
      }
    }
  }
  if (columns.length === 0) return '';
  const lines = [columns.map(cell).join(SEPARATOR)];
  for (const row of rows) lines.push(columns.map((c) => cell(row[c])).join(SEPARATOR));
  return lines.join(EOL) + EOL;
}

/**
 * The CSV of the rows a chart draws: `normalizeConfig`'s `data`, i.e. the inline
 * rows or the resolved dataset with `filters` applied — the same rows every
 * chart type's `buildTraces` is handed. A plan view therefore exports the bench
 * it shows, like the PNG beside it.
 */
export function chartDataToCsv(config: ChartConfig, getDataset?: DatasetResolver): string {
  return toCsv(normalizeConfig(config, getDataset).data);
}

/**
 * Hands `csv` to the browser as `<filename>.csv`. No network: a Blob URL and a
 * temporary anchor. The BOM makes Excel read the text as UTF-8 (accents in
 * category labels). The URL is revoked on the next tick, once the click has
 * started the download.
 */
export function downloadCsv(csv: string, filename: string): void {
  const url = URL.createObjectURL(new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${filename}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
